import React, { useEffect, useRef, useState } from "react";
import { LO_APP_ID, LO_COMPONENTS, SF_INSTANCE_URL } from "../config.js";

/**
 * Mounts a single Lightning Out 2.0 root component (c-app-root) and bridges
 * attributes + events between React state and the LWC.
 *
 *   React → LWC (attributes, all kebab-case on the DOM element):
 *     view          'list' | 'detail'
 *     keyword       search keyword (list view)
 *     record-id     selected record Id (detail view)
 *     record-name   selected record Name (detail view seed label)
 *     sobject-type  selected sobject (detail view)
 *
 *   LWC → React (CustomEvents, bubbles + composed, cross the iframe):
 *     recordselect  { id, name, sobjectType }  — row click in list view
 *     hostnavigate  { target: 'home' }         — back button in detail view
 */
export default function LightningOutHost({
    keyword,
    mode,
    selectedRecord,
    onRecordSelect,
    onHostNavigate,
    frontdoorEndpoint = "/api/get-frontdoor-url",
    requireCookieWarmup = false,
}) {
    const frameRef = useRef(null);
    const lwcRef = useRef(null);
    const [status, setStatus] = useState("Initializing…");
    const [error, setError] = useState(null);
    // Gate: until cookies are warmed (or the gate is disabled), don't mount.
    const [cookieGateDone, setCookieGateDone] = useState(!requireCookieWarmup);
    const [gateBusy, setGateBusy] = useState(false);
    const [gateNote, setGateNote] = useState(null);

    // Open a short popup to frontdoor.jsp so the browser records a top-level
    // user interaction with the Salesforce origin (Safari ITP, Chrome 3PCD
    // heuristics) and sets the session cookie as first-party. Subsequent
    // iframe loads can then read those cookies even in stricter cookie modes.
    async function warmCookies() {
        setGateBusy(true);
        setGateNote(null);
        try {
            // 1. Best effort: requestStorageAccessFor (Chrome w/ Related Website
            // Sets). Usually rejects without a RWS declaration — that's fine,
            // we'll fall through to the popup warmup.
            if (typeof document.requestStorageAccessFor === "function") {
                try {
                    await document.requestStorageAccessFor(SF_INSTANCE_URL);
                    setGateNote(
                        "Storage access granted via requestStorageAccessFor.",
                    );
                } catch {
                    /* ignore; try popup next */
                }
            }

            // 2. Fetch a dedicated frontdoor URL for the popup warmup. The
            // regular mount path will fetch another fresh one because OTPs
            // are single-use.
            const res = await fetch(frontdoorEndpoint, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
            });
            if (!res.ok) {
                throw new Error(
                    `Warmup frontdoor request failed: ${res.status}`,
                );
            }
            const { frontdoor_uri } = await res.json();

            const popup = window.open(
                frontdoor_uri,
                "sf-cookie-warmup",
                "width=520,height=420,top=120,left=120",
            );
            if (!popup) {
                throw new Error(
                    "Popup blocked. Allow popups for this site and try again.",
                );
            }

            // Give the popup ~2.5s to reach frontdoor.jsp, consume the OTP, and
            // receive the Set-Cookie response from Salesforce. We can't read
            // cross-origin document state, so this is a timed wait.
            await new Promise((r) => setTimeout(r, 2500));
            try {
                popup.close();
            } catch {
                /* ignore */
            }

            setGateNote(
                (prev) =>
                    (prev ? prev + " " : "") +
                    "Cookies warmed via popup. Loading Lightning Out…",
            );
            setCookieGateDone(true);
        } catch (e) {
            setError(e.message);
        } finally {
            setGateBusy(false);
        }
    }

    // Latest-callback refs so the one-time listeners always call current fns.
    const onRecordSelectRef = useRef(onRecordSelect);
    const onHostNavigateRef = useRef(onHostNavigate);
    useEffect(() => {
        onRecordSelectRef.current = onRecordSelect;
    }, [onRecordSelect]);
    useEffect(() => {
        onHostNavigateRef.current = onHostNavigate;
    }, [onHostNavigate]);

    // One-time bootstrap — gated on cookie warmup when required.
    useEffect(() => {
        if (!cookieGateDone) return;
        let cancelled = false;

        async function waitForSdk(timeoutMs = 10000) {
            const start = Date.now();
            while (!customElements.get("lightning-out-application")) {
                if (Date.now() - start > timeoutMs) {
                    throw new Error("Lightning Out SDK load timeout");
                }
                await new Promise((r) => setTimeout(r, 100));
            }
        }

        (async () => {
            try {
                setStatus("📡 Getting frontdoor URL…");
                const res = await fetch(frontdoorEndpoint, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                });
                if (res.status === 401) {
                    setError("Session expired. Please log in again.");
                    return;
                }
                if (!res.ok)
                    throw new Error(`Frontdoor request failed: ${res.status}`);
                const { frontdoor_uri } = await res.json();
                if (cancelled) return;

                setStatus("⏳ Waiting for Lightning Out SDK…");
                await waitForSdk();
                if (cancelled) return;

                setStatus("🔐 Creating Lightning Out elements…");
                const frame = frameRef.current;
                frame.innerHTML = "";

                const loApp = document.createElement(
                    "lightning-out-application",
                );
                loApp.setAttribute("app-id", LO_APP_ID);
                loApp.setAttribute("frontdoor-url", frontdoor_uri);
                loApp.setAttribute("components", LO_COMPONENTS);
                frame.appendChild(loApp);

                // Single root LWC — handles view switching internally.
                const el = document.createElement("c-app-root");
                el.setAttribute("view", mode || "list");
                if (keyword) el.setAttribute("keyword", keyword);
                if (selectedRecord?.id)
                    el.setAttribute("record-id", selectedRecord.id);
                if (selectedRecord?.name)
                    el.setAttribute("record-name", selectedRecord.name);
                if (selectedRecord?.sobjectType)
                    el.setAttribute("sobject-type", selectedRecord.sobjectType);

                el.addEventListener("recordselect", (e) => {
                    if (e.detail) onRecordSelectRef.current?.(e.detail);
                });
                el.addEventListener("hostnavigate", (e) => {
                    onHostNavigateRef.current?.(e.detail || { target: "home" });
                });

                frame.appendChild(el);
                lwcRef.current = el;

                setStatus("🔗 Lightning Out mounted — waiting for component…");
            } catch (e) {
                setError(e.message);
            }
        })();

        const onReady = () => setStatus("✅ Lightning Out session established");
        const onCompReady = () => setStatus("🎉 LWC rendered");
        const onAppErr = (e) =>
            setError("Application error: " + (e.detail?.message || "unknown"));
        const onCompErr = (e) =>
            setError("Component error: " + (e.detail?.message || "unknown"));

        window.addEventListener("lo.application.ready", onReady);
        window.addEventListener("lo.component.ready", onCompReady);
        window.addEventListener("lo.application.error", onAppErr);
        window.addEventListener("lo.component.error", onCompErr);

        return () => {
            cancelled = true;
            window.removeEventListener("lo.application.ready", onReady);
            window.removeEventListener("lo.component.ready", onCompReady);
            window.removeEventListener("lo.application.error", onAppErr);
            window.removeEventListener("lo.component.error", onCompErr);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cookieGateDone]);

    // Sync React state → LWC attributes. No remount, no iframe churn.
    useEffect(() => {
        const el = lwcRef.current;
        if (!el) return;
        el.setAttribute("view", mode || "list");
        if (keyword) el.setAttribute("keyword", keyword);
        else el.removeAttribute("keyword");
        if (selectedRecord?.id) el.setAttribute("record-id", selectedRecord.id);
        else el.removeAttribute("record-id");
        if (selectedRecord?.name)
            el.setAttribute("record-name", selectedRecord.name);
        else el.removeAttribute("record-name");
        if (selectedRecord?.sobjectType)
            el.setAttribute("sobject-type", selectedRecord.sobjectType);
        else el.removeAttribute("sobject-type");
    }, [mode, keyword, selectedRecord]);

    return (
        <>
            {error ? (
                <div className="status error">❌ {error}</div>
            ) : cookieGateDone ? (
                <div className="status">{status}</div>
            ) : (
                <div className="cookie-gate">
                    <strong>Grant Salesforce cookie access</strong>
                    <p>
                        Safari/private windows block third-party cookies by
                        default, which prevents the Lightning Out iframe from
                        reaching an authenticated session. Click below to
                        briefly open Salesforce in a popup so the browser
                        records a top-level interaction with the org — the
                        iframe will then load normally.
                    </p>
                    <button
                        className="btn"
                        onClick={warmCookies}
                        disabled={gateBusy}
                    >
                        {gateBusy
                            ? "Warming cookies…"
                            : "Enable Salesforce access"}
                    </button>
                    {gateNote && (
                        <div className="meta" style={{ marginTop: 8 }}>
                            {gateNote}
                        </div>
                    )}
                </div>
            )}
            <div className="lo-frame" ref={frameRef} />
        </>
    );
}
